import Button from 'ext-ui/components/Button/Button'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'
import type { ActivityEvent } from 'src/page-components/Accounts/AccountActivityTab/accountActivityDemoData'
import ActivityEventRow from 'src/page-components/Accounts/AccountActivityTab/components/ActivityEventRow/ActivityEventRow'
import * as style from 'src/page-components/Accounts/AccountActivityTab/components/ActivityFeed/ActivityFeed.style'

interface ActivityFeedProps {
  /** Accumulated across every page loaded so far, newest first. */
  events: ActivityEvent[]
  isLoading: boolean
  isLoadingMore: boolean
  hasError: boolean
  hasMore: boolean
  onLoadMore: () => void
  /** Re-requests the first page. Rendered as the recovery from a failed load. */
  onRetry: () => void
}

/**
 * The activity feed (EX-40600) — one vertically divided, newest-first list.
 *
 * Read-only by design: no unread badges, bulk actions, dismissal, or filtering.
 * This is audit history, not a notification inbox, and that is a guardrail
 * rather than a scoping decision.
 *
 * ## Pagination
 *
 * The feed is cursor-paginated and expands IN PLACE — "Load more activity"
 * appends the next page beneath the existing rows and the control removes
 * itself once the server reports a terminal page. It must not navigate or swap
 * the list; the design's guardrail for the sibling Statements list is the same,
 * and this feed is the surface where a user is comparing one event against
 * another further down.
 *
 * Explicit control rather than infinite scroll: the design leaves the loading
 * strategy open, and an audit feed that loads on scroll makes it impossible to
 * reach anything below it, while also firing requests a user never asked for.
 *
 * ## Why the busy control is `aria-disabled`, not `disabled`
 *
 * `ext-ui`'s `Button` forwards `disabled` to the native attribute, and per the
 * HTML spec a browser UNFOCUSES an element the moment it becomes disabled —
 * Chrome, Firefox, and Safari all drop `document.activeElement` to `<body>`.
 * Disabling the control while its own page loads would therefore throw focus
 * away on EVERY "Load more" click, including the intermediate pages, and it
 * would do it silently: jsdom does not implement blur-on-disable, so a
 * `toHaveFocus()` assertion stays green while real browsers fail WCAG 2.4.3.
 *
 * `aria-disabled` reports the busy state to assistive tech without removing the
 * element from the focus order, so the user keeps their place. The press is
 * ignored in `handleLoadMore` instead, backed by the `isFetchingRef` lock in
 * `useAccountActivityData` — two guards, neither of which moves focus.
 *
 * ## Focus and announcements
 *
 * Loading the LAST page destroys the control that was just activated:
 * `nextCursor: null` flips `hasMore` false in the same update that appends its
 * rows. Focus would fall to `<body>`, so it moves to the first newly appended
 * row instead — the same guard the sibling `StatementList` applies.
 *
 * Announcements live in a separate visually-hidden `role="status"` rather than
 * on the list. Two reasons. A live region has to be REGISTERED before content
 * lands in it, and an early-returning list is created and populated in the same
 * commit, so screen readers either miss the change or read the whole feed. And
 * on the terminal page focus moves INTO the list, so a live list would announce
 * the same rows twice — once as an addition, once as a focus change. Separating
 * them lets the status report what happened and focus report where you are.
 */
const ActivityFeed: React.FC<ActivityFeedProps> = ({
  events,
  isLoading,
  isLoadingMore,
  hasError,
  hasMore,
  onLoadMore,
  onRetry,
}) => {
  const translate = useTranslate()

  // Only the row we may need to focus is tracked, keyed by index, so the list
  // does not have to rebuild a ref array on every render.
  const rowRefs = useRef<Record<number, HTMLLIElement | null>>({})
  // Index of the first row of the page currently being fetched, or null when no
  // "Load more" is in flight. A ref rather than state: it is written from an
  // event handler and read from an effect, and must not itself cause a render.
  const pendingFocusIndexRef = useRef<number | null>(null)
  // Count only — the translated sentence is built at render, so `translate`
  // never has to be an effect dependency (it is not referentially stable).
  const [appendedCount, setAppendedCount] = useState(0)

  const handleLoadMore = () => {
    // The control stays focusable while busy (see the docblock), so it can be
    // pressed again mid-flight. Ignore that here rather than disabling it.
    if (isLoadingMore) {
      return
    }
    pendingFocusIndexRef.current = events.length
    onLoadMore()
  }

  useEffect(() => {
    const index = pendingFocusIndexRef.current
    if (index === null || isLoadingMore) {
      return
    }
    pendingFocusIndexRef.current = null

    const appended = events.length - index
    if (appended <= 0) {
      // The page failed; nothing was appended and the error message below is
      // what the user needs, not a "0 more" announcement.
      return
    }
    setAppendedCount(appended)

    // Only when the control unmounted. While it survives, the user's focus is
    // still on it and moving them would be the more disruptive behaviour.
    if (!hasMore) {
      rowRefs.current[index]?.focus()
    }
  }, [events.length, hasMore, isLoadingMore])

  const showEmpty = !isLoading && !hasError && events.length === 0
  const showFirstPageError = hasError && events.length === 0

  return (
    <div data-testid="activityFeed">
      {/*
        Rendered unconditionally so the region is registered before any content
        arrives — a live region created in the same commit as its content is
        unreliable across screen readers.
      */}
      <p
        css={style.visuallyHidden}
        role="status"
        data-testid="activityFeed-status"
      >
        {appendedCount > 0
          ? translate('${count} more events loaded', {
              count: String(appendedCount),
            })
          : ''}
      </p>

      {isLoading && (
        <p css={style.message} data-testid="activityFeed-loading">
          {translate('Loading activity…')}
        </p>
      )}

      {showFirstPageError && (
        <div css={style.errorState} data-testid="activityFeed-error">
          <p css={style.message} role="alert">
            {translate('We couldn’t load activity for this card.')}
          </p>
          <Button
            variant="text"
            onClick={onRetry}
            data-testid="activityFeed-retry"
          >
            {translate('Try again')}
          </Button>
        </div>
      )}

      {showEmpty && (
        <p css={style.message} data-testid="activityFeed-empty">
          {translate('No activity on this card yet.')}
        </p>
      )}

      {events.length > 0 && (
        <ul css={style.list}>
          {events.map((event, index) => (
            <ActivityEventRow
              key={event.id}
              event={event}
              rowRef={(node) => {
                rowRefs.current[index] = node
              }}
            />
          ))}
        </ul>
      )}

      {hasError && events.length > 0 && (
        <p
          css={style.message}
          role="alert"
          data-testid="activityFeed-moreError"
        >
          {translate('We couldn’t load more activity. Try again later.')}
        </p>
      )}

      {hasMore && (
        <div css={style.loadMore}>
          <Button
            variant="text"
            onClick={handleLoadMore}
            aria-disabled={isLoadingMore}
            data-testid="activityFeed-loadMore"
          >
            {isLoadingMore
              ? translate('Loading…')
              : translate('Load more activity')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default ActivityFeed
